import { z } from "zod";
import { actionAvailabilitySchema, idSchema, instantSchema, moneySchema, reasonSchema } from "@amber/contracts";
import { HEALTH_ORDER } from "./health.js";
import {
  cadenceTierSchema,
  sourceKindSchema,
  type SOURCE_KINDS,
  type gapNextStepSchema,
  type gapResolutionKindSchema,
} from "./sources.js";

export const MONITORING_ACTIONS = {
  addSource: "collection.add_source",
  editSource: "collection.edit_source",
  scanNow: "collection.scan_now",
  pauseSource: "collection.pause_source",
  resumeSource: "collection.resume_source",
  markQuiet: "collection.mark_expected_quiet",
  monitor: "collection.monitor_source",
} as const;

export const EDITOR_SOURCES_REASON = "Only Editors and above can scan, add or change sources";
export const OPERATOR_SOURCE_PAUSE_REASON = "Only Operators and Admins can pause or resume sources";

export const sourceHealthKeySchema = z.enum(HEALTH_ORDER);
export type SourceHealthKey = z.infer<typeof sourceHealthKeySchema>;

export const SOURCE_KIND_LABELS: Record<(typeof SOURCE_KINDS)[number], string> = {
  channel: "Channel or account",
  playlist: "Channel or account",
  account: "Channel or account",
  programme: "Programme",
  feed: "Feed",
  official_record: "Official record",
  website: "Website",
  news_search: "News search",
};

export const TIER_LABELS = { priority: "Priority", standard: "Standard", low: "Low" } as const;

export const RELATIONSHIPS = ["own", "office", "organisation", "host", "guest", "official_record", "coverage"] as const;
export const relationshipSchema = z.enum(RELATIONSHIPS);
export type Relationship = z.infer<typeof relationshipSchema>;
export const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  own: "Own",
  office: "Office",
  organisation: "Organisation",
  host: "Host",
  guest: "Guest",
  official_record: "Official record",
  coverage: "Coverage",
};
export const OWN_RELATIONSHIPS: readonly Relationship[] = ["own", "office", "organisation", "official_record"];

export const sourcePersonSchema = z.strictObject({
  profile_id: idSchema("prf"),
  relationship: relationshipSchema,
  valid_from: z.iso.date().nullable().optional(),
  valid_to: z.iso.date().nullable().optional(),
});
export type SourcePerson = z.infer<typeof sourcePersonSchema>;

export const knownBreakSchema = z.strictObject({
  id: idSchema("brk"),
  kind: z.enum(["recorded_break", "expected_quiet"]),
  from: z.iso.date(),
  to: z.iso.date().nullable(),
  label: z.string().min(1),
  by: z.unknown(),
  ended_at: instantSchema.nullable(),
});
export type KnownBreak = z.infer<typeof knownBreakSchema>;

export const addSourceRequestSchema = z.strictObject({
  url: z.string().trim().min(1, "Paste the address of the source"),
  title: z.string().trim().max(300).optional(),
  kind: sourceKindSchema.optional(),
  people: z.array(sourcePersonSchema).default([]),
  plans: z.array(z.strictObject({ profile_id: idSchema("prf"), required: z.boolean() })).default([]),
  cadence_tier: cadenceTierSchema.optional(),
  speaker_id: z
    .string()
    .trim()
    .regex(/^\d+$/, "The speaker ID is the number the official record prints for the speaker")
    .optional(),
  known_breaks: z
    .array(z.strictObject({ from: z.iso.date(), to: z.iso.date(), label: z.string().trim().min(1) }))
    .default([]),
  reason: reasonSchema,
});
export type AddSourceRequest = z.infer<typeof addSourceRequestSchema>;

export const scanNowRequestSchema = z.strictObject({
  reason: reasonSchema,
  base_revision: z.number().int().positive(),
});
export const sourceReasonRequestSchema = z.strictObject({
  reason: reasonSchema,
  base_revision: z.number().int().positive(),
});
export const resumeSourceRequestSchema = sourceReasonRequestSchema.extend({ catch_up: z.boolean().default(true) });

export const timelineMarkSchema = z.strictObject({
  kind: z.enum([
    "complete",
    "gap",
    "read_not_complete",
    "not_yet_scanned",
    "paused",
    "not_monitored",
    "accepted_gap",
    "extending",
  ]),
  from: instantSchema,
  to: instantSchema,
  label: z.string(),
  gap_id: idSchema("gap").nullable(),
});
export type TimelineMark = z.infer<typeof timelineMarkSchema>;

export const lastScanSchema = z.strictObject({
  at: instantSchema,
  result: z.enum(["complete", "partial", "failed", "running", "queued"]),
  label: z.string(),
});

export const nextScanSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("at"), at: instantSchema, late: z.boolean() }),
  z.strictObject({ kind: z.literal("scanning") }),
  z.strictObject({ kind: z.literal("paused") }),
  z.strictObject({ kind: z.literal("none"), text: z.string() }),
]);

export const sourcePersonViewSchema = z.strictObject({
  id: idSchema("prf"),
  name: z.string(),
  relationship: relationshipSchema.nullable(),
  in_plan: z.boolean(),
  pending: z.boolean(),
  required: z.boolean(),
});

export const sourceRowSchema = z.strictObject({
  id: idSchema("src"),
  title: z.string(),
  url: z.string(),
  platform: z.strictObject({ key: z.string(), label: z.string(), collected: z.boolean() }),
  kind: sourceKindSchema,
  kind_label: z.string(),
  people: z.array(sourcePersonViewSchema),
  shared_plan: z.strictObject({ applied: z.boolean(), pending: z.boolean() }).nullable(),
  health: z.strictObject({
    state: sourceHealthKeySchema,
    label: z.string(),
    reason: z.string().nullable(),
    platform_wide: z.boolean(),
  }),
  tier: cadenceTierSchema,
  tier_label: z.string(),
  complete_through: instantSchema.nullable(),
  complete_back_to: instantSchema.nullable(),
  extension: z.strictObject({ back_to: instantSchema, waiting: z.string().nullable() }).nullable(),
  last_scan: lastScanSchema.nullable(),
  next_scan: nextScanSchema,
  items_7d: z.number().int().nonnegative(),
  items_total: z.number().int().nonnegative(),
  download_problems: z.number().int().nonnegative(),
  open_gaps: z.number().int().nonnegative(),
  required: z.boolean(),
  pause: z
    .strictObject({
      id: idSchema("pau"),
      by: z.string(),
      at: instantSchema,
      reason: z.string(),
      scope: z.enum(["source", "person"]),
      person: z.string().nullable(),
    })
    .nullable(),
  backfill: z
    .strictObject({ days_read: z.number().nonnegative(), days_total: z.number().nonnegative(), kind: z.string() })
    .nullable(),
  usual_gap_days: z.number().nullable(),
  timeline: z.array(timelineMarkSchema),
  timeline_label: z.string(),
  revision: z.number().int().positive(),
});
export type SourceRow = z.infer<typeof sourceRowSchema>;

export const platformRowSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  collection_state: z.enum(["collected", "known_not_collected", "future_only"]),
  health: z.strictObject({ state: sourceHealthKeySchema.nullable(), label: z.string(), affected: z.string() }),
  issue: z
    .strictObject({
      id: idSchema("pwi"),
      cause: z.string(),
      title: z.string(),
      next_step: z.string(),
      expected_reset: instantSchema.nullable(),
    })
    .nullable(),
  daily: z.array(z.number().int().nonnegative()),
  items_7d: z.number().int().nonnegative(),
  items_30d: z.number().int().nonnegative(),
  items_total: z.number().int().nonnegative(),
  archive_bytes: z.number().int().nonnegative(),
  download_problems: z.number().int().nonnegative(),
  open_gaps: z.number().int().nonnegative(),
  last_new_item: instantSchema.nullable(),
  sources: z.number().int().nonnegative(),
  people: z.number().int().nonnegative(),
  scanning: z.boolean(),
});
export type PlatformRow = z.infer<typeof platformRowSchema>;

export const GAP_NEXT_STEP_LABELS: Record<z.infer<typeof gapNextStepSchema> | "needs_person", string> = {
  automatic_retry: "Automatic retry at the next scan",
  backfill_queued: "Backfill queued",
  backfill_running: "Backfill running",
  catch_up_queued: "Catch-up scan queued",
  wait_for_source: "Waiting for the source",
  accept_as_permanent: "Accepted as permanent",
  get_access: "Needs access: get it in a documented way, or accept as permanent",
  not_listed: "Not listed by the source: request a backfill from another route, or accept as permanent",
  needs_person: "Needs someone",
};

export const GAP_RESOLUTION_LABELS: Record<z.infer<typeof gapResolutionKindSchema>, string> = {
  closed_by_scan: "Closed automatically: a later scan read the whole interval",
  closed_by_backfill: "Closed automatically: the backfill read the whole interval",
  accepted_permanent: "Accepted as permanent",
  resolved_with_note: "Resolved with a note",
};

export const gapRowSchema = z.strictObject({
  id: idSchema("gap"),
  source: z.strictObject({ id: idSchema("src"), title: z.string() }),
  people: z.array(z.strictObject({ id: idSchema("prf"), name: z.string() })),
  platform: z.strictObject({ key: z.string(), label: z.string() }),
  from: instantSchema.nullable(),
  to: instantSchema.nullable(),
  reason: z.string(),
  opened_at: instantSchema,
  handling: z.enum(["automatic", "needs_person"]),
  next_step: z.string(),
  task_id: idSchema("tsk").nullable(),
  resolved: z
    .strictObject({ at: instantSchema, how: z.string(), by: z.string(), note: z.string().nullable() })
    .nullable(),
  from_scan: z.boolean(),
  recovery: z
    .strictObject({
      job_id: z.string(),
      kind: z.enum(["gap_backfill", "catch_up"]),
      state: z.string(),
      days_read: z.number().nonnegative(),
      days_total: z.number().nonnegative(),
    })
    .nullable(),
});
export type GapRow = z.infer<typeof gapRowSchema>;

export const SOURCES_TABS = ["platforms", "sources", "gaps"] as const;
export const sourcesTabSchema = z.enum(SOURCES_TABS);

export const sourcesQuerySchema = z.strictObject({
  tab: sourcesTabSchema.optional(),
  platform: z.string().optional(),
  health: z.string().optional(),
  person: z.string().optional(),
  q: z.string().optional(),
  source: z.string().optional(),
  window: z.coerce
    .number()
    .int()
    .refine((w) => w === 30 || w === 90 || w === 180)
    .optional(),
  gaps: z.enum(["open", "resolved"]).optional(),
});
export type SourcesQuery = z.infer<typeof sourcesQuerySchema>;

export const sourcesResponseSchema = z.strictObject({
  platforms: z.array(platformRowSchema),
  sources: z.array(sourceRowSchema),
  gaps: z.array(gapRowSchema),
  counts: z.strictObject({
    sources: z.number().int().nonnegative(),
    by_health: z.record(z.string(), z.number().int().nonnegative()),
    open_gaps: z.number().int().nonnegative(),
    resolved_gaps_30d: z.number().int().nonnegative(),
    scanning: z.array(z.string()),
    next_scan_at: instantSchema.nullable(),
  }),
  banners: z.array(z.strictObject({ platform: z.string(), text: z.string(), key: z.string() })),
  window: z.object({ from: instantSchema, to: instantSchema, days: z.number().int() }),
  extension: z.strictObject({ text: z.string(), href: z.string() }).nullable(),
  actions: z.strictObject({
    add_source: actionAvailabilitySchema,
    scan_now: actionAvailabilitySchema,
    pause: actionAvailabilitySchema,
    edit_source: actionAvailabilitySchema,
    gap_decision: actionAvailabilitySchema,
    cancel_backfill: actionAvailabilitySchema,
  }),
});
export type SourcesResponse = z.infer<typeof sourcesResponseSchema>;

export const scanViewSchema = z.strictObject({
  id: idSchema("scn"),
  at: instantSchema,
  kind: z.string(),
  result: z.string(),
  items_found: z.number().int().nonnegative(),
  items_new: z.number().int().nonnegative(),
  reach: z.string().nullable(),
  reason: z.string().nullable(),
  engine_url: z.string().nullable(),
});

export const SOURCE_YEAR_STATES = ["complete", "complete_with_accepted_gaps", "partial", "not_collected"] as const;
export const sourceYearSchema = z.strictObject({
  year: z.number().int(),
  from: instantSchema,
  to: instantSchema,
  state: z.enum(SOURCE_YEAR_STATES),
  label: z.string(),
  detail: z.string(),
  days_total: z.number().int().nonnegative(),
  days_complete: z.number().int().nonnegative(),
  days_open_gap: z.number().int().nonnegative(),
  days_accepted_gap: z.number().int().nonnegative(),
});
export type SourceYear = z.infer<typeof sourceYearSchema>;

export const sourceDetailSchema = z.strictObject({
  source: sourceRowSchema,
  canonical_url: z.string(),
  monitored_since: instantSchema.nullable(),
  /** DI-9, DI-96: what the supplier behind a news-search source can search, as recorded at its last scan. */
  supplier: z
    .strictObject({
      name: z.string(),
      coverage: z.string(),
      limits: z.string(),
      recorded_at: instantSchema,
    })
    .nullable(),
  owner_team: z.string().nullable(),
  quota_group: z.string().nullable(),
  breaks: z.array(knownBreakSchema),
  plans: z.array(
    z.strictObject({
      plan_id: idSchema("pln"),
      owner: z.string(),
      kind: z.enum(["person", "shared_source"]),
      tier: cadenceTierSchema,
      required: z.boolean(),
      applied: z.boolean(),
      pending: z.boolean(),
    }),
  ),
  scans: z.array(scanViewSchema),
  gaps: z.array(gapRowSchema),
  years: z.array(sourceYearSchema),
  history: z.array(
    z.strictObject({
      at: instantSchema,
      actor: z.string(),
      action: z.string(),
      reason: z.string().nullable(),
      changes: z.array(z.strictObject({ label: z.string(), before: z.string(), after: z.string() })),
    }),
  ),
  scan_preview: z.strictObject({ people: z.array(z.string()), estimate: moneySchema, note: z.string() }),
  actions: z.strictObject({
    scan_now: actionAvailabilitySchema,
    pause: actionAvailabilitySchema,
    resume: actionAvailabilitySchema,
    mark_quiet: actionAvailabilitySchema,
    edit: actionAvailabilitySchema,
    monitor: actionAvailabilitySchema,
    gap_decision: actionAvailabilitySchema,
    cancel_backfill: actionAvailabilitySchema,
  }),
});
export type SourceDetail = z.infer<typeof sourceDetailSchema>;

export const planPreviewRequestSchema = z.strictObject({
  sources: z.array(z.strictObject({ source_id: idSchema("src"), cadence_tier: cadenceTierSchema })),
  back_to: z.iso.date(),
  owner_plan_id: idSchema("pln").nullable(),
});

export const planPreviewSchema = z.strictObject({
  items_per_month: z.number().nonnegative(),
  cost_per_month: moneySchema,
  history_items: z.number().nonnegative(),
  history_cost: moneySchema,
  scans_per_month: z.number().nonnegative(),
  quota_units_per_month: z.number().nonnegative(),
  sources: z.array(
    z.strictObject({
      source_id: idSchema("src"),
      title: z.string(),
      items_per_month: z.number().nonnegative(),
      cost_per_month: moneySchema,
      share: z.number().positive(),
      basis: z.string(),
      unreachable: z.string().nullable(),
    }),
  ),
  unreachable: z.array(z.string()),
});
export type PlanPreview = z.infer<typeof planPreviewSchema>;

export const sourceOptionSchema = z.strictObject({
  id: idSchema("src"),
  title: z.string(),
  platform: z.string(),
  platform_label: z.string(),
  kind_label: z.string(),
  default_tier: cadenceTierSchema,
  collected: z.boolean(),
  people: z.array(z.strictObject({ id: idSchema("prf"), relationship: relationshipSchema.nullable() })),
});
export type SourceOption = z.infer<typeof sourceOptionSchema>;

export const dismissedBannersSchema = z.array(z.string()).catch([]);
